import { Formik, Form, Field } from "formik";
import { FC, useState } from "react";
import {
    FeatureList,
    Permissions,
    Project,
    ProjectProduct,
    StateActionPermission,
    TypeAction,
} from "../../../models/models";
import {
    useCreateProjectMutation,
    useUpdateProjectMutation,
} from "../../../redux/RTKQuery/api";
import ActionButtons from "../../../assets/components/ActionButtons/ActionButtons";
import * as Yup from "yup";
import TextInput from "../../../assets/components/Inputs/TextInput/TextInput";
import "./ProjectSubRow.scss";
import { ProjectSubRowFeatures } from "./ProjectSubRowFeatures/ProjectSubRowFeatures";
import { useAppSelector } from "../../../assets/hooks/hooks";
import { hasPermission } from "../../../assets/helpers/helpers";
import { actionText } from "../../../text";
import { useNotification } from "../../../assets/hooks/useNotification";

type formField = {
    id?: number;
    name: string;
};

export interface EditRowProject extends Project {
    closeFormHandler: (id: number) => void;
}

const validateSchema = Yup.object().shape({
    name: Yup.string().required("Required"),
});

interface PropsShowInstalledProjects {
    products: ProjectProduct;
}

const ShowInstalledProjects: FC<PropsShowInstalledProjects> = ({
    products,
}) => {
    return (
        <ul key="list_products" className="products">
            {products.map((product, id) => (
                <li key={id} className="product">
                    {product.name}
                </li>
            ))}
        </ul>
    );
};

const ProjectsSubRow: FC<EditRowProject> = ({
    id,
    name,
    features = [],
    products = [],
    closeFormHandler,
}) => {
    const [updateProject] = useUpdateProjectMutation({
        fixedCacheKey: "update_project",
    });
    const [createProject] = useCreateProjectMutation({
        fixedCacheKey: "create_project",
    });

    const { ConfirmationMessageBox } = useNotification();
    const user = useAppSelector((state) => state.initializeApp);
    const isAccessPermission = hasPermission(
        user.permissions,
        Permissions.PROJECT_EDIT
    );

    const [typeActionProject] = useState<TypeAction>(
        id === -1 ? { type: "create" } : { type: "update" }
    );

    const [projectFeatures, setNewFeaturesList] =
        useState<FeatureList>(features);
    const [projectProduct] = useState<ProjectProduct>(products);

    const getFeaturesListHandler = (features: FeatureList): void => {
        setNewFeaturesList(features);
    };

    const saveProjectHandler = async (values: formField) => {
        const prepareFeaturesForExport = projectFeatures.map((feature) => {
            if (feature.id < 0) {
                return { id: 0, name };
            }
            return feature;
        });

        const dataForExport = {
            ...values,
            features: prepareFeaturesForExport,
        };

        ConfirmationMessageBox({
            textContent: actionText(
                typeActionProject.type as StateActionPermission,
                "project"
            ),
        }).then((value) => {
            if (value === "yes") {
                if (typeActionProject.type === "create") {
                    delete dataForExport.id;
                    createProject(dataForExport);
                } else if (typeActionProject.type === "update") {
                    updateProject(dataForExport);
                }
            }
        });
    };

    return (
        <Formik
            initialValues={{
                name,
                id,
            }}
            validationSchema={validateSchema}
            onSubmit={saveProjectHandler}
        >
            {({ isSubmitting, isValid, values }) => {
                return (
                    <Form className="form_feature">
                        <div className="form_feature__col">
                            <div className="form_feature__row">
                                <Field
                                    id="name"
                                    label="Project Name *"
                                    name="name"
                                    as={TextInput}
                                    readOnly={!isAccessPermission}
                                />
                            </div>
                            <ShowInstalledProjects
                                key="projects_list"
                                products={projectProduct}
                            />
                        </div>

                        <ProjectSubRowFeatures
                            projectFeatures={projectFeatures}
                            getFeaturesList={getFeaturesListHandler}
                        />

                        <div className="form_feature__footer">
                            <ActionButtons
                                className="form_feature__button_submit"
                                actionButtons={[
                                    {
                                        type: "button",
                                        disabled: isSubmitting,
                                        name: "close",
                                        role: "secondary",
                                        onClick: () => {
                                            closeFormHandler(id);
                                        },
                                    },
                                    {
                                        hidden: !isAccessPermission,
                                        type: "submit",
                                        disabled: isSubmitting || !isValid,
                                        name: "save",
                                        role: "primary",
                                    },
                                ]}
                            />
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};

export default ProjectsSubRow;
